import { useState } from 'react';
import { Star, ThumbsUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Button } from '../components/ui/button';
import { testimonials } from '../data/mock';

export default function Reviews() {
  // TEMPORARY HIDE FLAG - Use key phrase "SHOW_COMMUNITY_SECTIONS" to restore
  const HIDE_REVIEWS_PAGE = true;
  
  const [visibleReviews, setVisibleReviews] = useState(6);

  const loadMore = () => {
    setVisibleReviews(prev => Math.min(prev + 3, testimonials.length));
  };

  const overallRating = (testimonials.reduce((sum, t) => sum + t.rating, 0) / testimonials.length).toFixed(1);

  // Hide page temporarily
  if (HIDE_REVIEWS_PAGE) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center px-4">
          <h1 className="text-4xl font-bold text-[#193460] mb-4">Coming Soon</h1>
          <p className="text-gray-600 text-lg">This page is currently being updated. Check back soon!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <section className="bg-[#193460] text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">Community Reviews</h1>
          <p className="text-xl text-gray-200">
            See what our community members have to say about their repair cafe experience
          </p>
        </div>
      </section>

      {/* Rating Overview */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-2 border-[#E87B6A] shadow-xl">
            <CardContent className="p-8 text-center">
              <div className="flex flex-col md:flex-row items-center justify-center gap-8">
                <div>
                  <div className="text-6xl font-bold text-[#193460] mb-2">{overallRating}</div>
                  <div className="flex justify-center mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-6 h-6 fill-[#E87B6A] text-[#E87B6A]" />
                    ))}
                  </div>
                  <p className="text-gray-600">Based on {testimonials.length} reviews</p>
                </div>
                <div className="border-l-2 border-gray-300 pl-8 hidden md:block h-24"></div>
                <div className="text-center md:text-left">
                  <h3 className="text-2xl font-bold text-[#193460] mb-2">Excellent Service</h3>
                  <p className="text-gray-700 max-w-md">
                    Our community loves what we do! Join the hundreds of satisfied residents who've saved their items from the landfill.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.slice(0, visibleReviews).map((testimonial) => (
              <Card key={testimonial.id} className="border-2 border-gray-200 hover:border-[#E87B6A] transition-all duration-300 hover:shadow-xl transform hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    <Avatar className="w-14 h-14 mr-4 bg-[#193460]">
                      <AvatarFallback className="text-white font-bold text-lg">{testimonial.avatar}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <h4 className="font-semibold text-[#193460] text-lg">{testimonial.name}</h4>
                      <p className="text-sm text-gray-500">{testimonial.date}</p>
                    </div>
                  </div>
                  
                  <div className="flex mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-[#E87B6A] text-[#E87B6A]" />
                    ))}
                  </div>

                  <p className="text-gray-700 leading-relaxed mb-4">{testimonial.comment}</p>

                  <button className="flex items-center text-[#193460] hover:text-[#E87B6A] transition-colors duration-300 text-sm font-medium">
                    <ThumbsUp className="w-4 h-4 mr-1" />
                    Helpful
                  </button>
                </CardContent>
              </Card>
            ))}
          </div>

          {visibleReviews < testimonials.length && (
            <div className="text-center mt-12">
              <Button 
                onClick={loadMore}
                size="lg"
                variant="outline"
                className="border-2 border-[#193460] text-[#193460] hover:bg-[#193460] hover:text-white font-semibold px-8 transition-all duration-300"
              >
                Load More Reviews
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Leave Review CTA */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Card className="border-2 border-[#E87B6A] shadow-lg">
            <CardContent className="p-8">
              <Star className="w-16 h-16 text-[#E87B6A] mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-[#193460] mb-4">Share Your Experience</h2>
              <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                Have you attended one of our repair cafe events? We'd love to hear about your experience! Your feedback helps us improve and encourages others in the community.
              </p>
              <a href="/contact">
                <Button 
                  size="lg"
                  className="bg-[#E87B6A] hover:bg-[#d66a59] text-white font-semibold px-8 py-6 text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                >
                  Contact Us to Leave a Review
                </Button>
              </a>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
